import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface Decorations {
  type: string;
  intensity: string;
  colors: string[];
}

interface Theme {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  decorations: Decorations | null;
}

export function SeasonalDecorations() {
  const [theme, setTheme] = useState<Theme | null>(null);

  useEffect(() => {
    loadActiveTheme();
  }, []);

  const loadActiveTheme = async () => {
    try {
      const { data, error } = await supabase
        .from('site_themes')
        .select('*')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error loading theme:', error);
        return;
      }

      if (data) {
        setTheme({
          ...data,
          decorations: data.decorations as unknown as Decorations | null,
        });
      } else {
        setTheme(null);
      }
    } catch (error) {
      console.error('Error loading theme:', error);
    }
  };

  if (!theme || !theme.decorations || theme.decorations.type === 'none') {
    return null;
  }

  const { type, intensity } = theme.decorations;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {type === 'snowflakes' && <SnowflakesEffect intensity={intensity} />}
      {type === 'leaves' && <LeavesEffect intensity={intensity} />}
      {type === 'petals' && <PetalsEffect intensity={intensity} />}
      {type === 'hearts' && <HeartsEffect intensity={intensity} />}
      {type === 'confetti' && <ConfettiEffect intensity={intensity} />}
      {type === 'sunshine' && <SunshineEffect intensity={intensity} />}
      {type === 'easter' && <EasterEffect intensity={intensity} />}
      {type === 'halloween' && <HalloweenEffect intensity={intensity} />}
    </div>
  );
}

// Shared animation styles
const fallKeyframes = `
  @keyframes fall {
    0% {
      transform: translateY(-10vh) rotate(0deg) translateX(0);
      opacity: 1;
    }
    25% {
      transform: translateY(25vh) rotate(90deg) translateX(15px);
      opacity: 0.9;
    }
    50% {
      transform: translateY(50vh) rotate(180deg) translateX(-10px);
      opacity: 0.7;
    }
    75% {
      transform: translateY(80vh) rotate(270deg) translateX(15px);
      opacity: 0.5;
    }
    100% {
      transform: translateY(110vh) rotate(360deg) translateX(0);
      opacity: 0.3;
    }
  }
  
  @keyframes heart-fall {
    0% {
      transform: translateY(-10vh) scale(1) translateX(0);
      opacity: 0.8;
    }
    25% {
      transform: translateY(25vh) scale(1.1) translateX(10px);
      opacity: 0.9;
    }
    50% {
      transform: translateY(50vh) scale(0.9) translateX(-10px);
      opacity: 0.7;
    }
    75% {
      transform: translateY(80vh) scale(1.05) translateX(8px);
      opacity: 0.5;
    }
    100% {
      transform: translateY(110vh) scale(1) translateX(0);
      opacity: 0.3;
    }
  }
  
  @keyframes sway-fall {
    0% {
      transform: translateY(-10vh) rotate(0deg) translateX(0);
      opacity: 1;
    }
    25% {
      transform: translateY(25vh) rotate(45deg) translateX(20px);
      opacity: 0.9;
    }
    50% {
      transform: translateY(50vh) rotate(-10deg) translateX(-15px);
      opacity: 0.7;
    }
    75% {
      transform: translateY(80vh) rotate(30deg) translateX(10px);
      opacity: 0.5;
    }
    100% {
      transform: translateY(110vh) rotate(0deg) translateX(0);
      opacity: 0.3;
    }
  }
  
  @keyframes sparkle {
    0%, 100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    50% {
      opacity: 1;
      transform: scale(1.2);
    }
  }
`;

function getParticleCount(intensity: string): number {
  switch (intensity) {
    case 'subtle': return 15;
    case 'medium': return 30;
    case 'strong': return 50;
    default: return 30;
  }
}

function SnowflakesEffect({ intensity }: { intensity: string }) {
  const count = getParticleCount(intensity);
  
  return (
    <>
      <style>{fallKeyframes}</style>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="absolute text-foreground/25"
          style={{
            left: `${Math.random() * 100}%`,
            fontSize: `${12 + Math.random() * 16}px`,
            animation: `fall ${8 + Math.random() * 12}s linear infinite`,
            animationDelay: `${Math.random() * 10}s`,
            opacity: 0.6 + Math.random() * 0.4,
          }}
        >
          ❄
        </span>
      ))}
    </>
  );
}

function LeavesEffect({ intensity }: { intensity: string }) {
  const count = getParticleCount(intensity);
  const leaves = ['🍂', '🍁', '🍃'];
  
  return (
    <>
      <style>{fallKeyframes}</style>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            fontSize: `${16 + Math.random() * 12}px`,
            animation: `sway-fall ${10 + Math.random() * 15}s linear infinite`,
            animationDelay: `${Math.random() * 10}s`,
            opacity: 0.7 + Math.random() * 0.3,
          }}
        >
          {leaves[Math.floor(Math.random() * leaves.length)]}
        </span>
      ))}
    </>
  );
}

function PetalsEffect({ intensity }: { intensity: string }) {
  const count = getParticleCount(intensity);
  
  return (
    <>
      <style>{fallKeyframes}</style>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            fontSize: `${14 + Math.random() * 10}px`,
            animation: `sway-fall ${12 + Math.random() * 10}s linear infinite`,
            animationDelay: `${Math.random() * 8}s`,
            opacity: 0.6 + Math.random() * 0.4,
          }}
        >
          🌸
        </span>
      ))}
    </>
  );
}

function HeartsEffect({ intensity }: { intensity: string }) {
  const count = getParticleCount(intensity);
  const hearts = ['❤️', '💕', '💗'];
  
  return (
    <>
      <style>{fallKeyframes}</style>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            fontSize: `${12 + Math.random() * 14}px`,
            animation: `heart-fall ${10 + Math.random() * 12}s linear infinite`,
            animationDelay: `${Math.random() * 8}s`,
            opacity: 0.5 + Math.random() * 0.5,
          }}
        >
          {hearts[Math.floor(Math.random() * hearts.length)]}
        </span>
      ))}
    </>
  );
}

function ConfettiEffect({ intensity }: { intensity: string }) {
  const count = getParticleCount(intensity);
  const confetti = ['🎊', '🎉', '✨', '🎀'];
  
  return (
    <>
      <style>{fallKeyframes}</style>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            fontSize: `${14 + Math.random() * 12}px`,
            animation: `sway-fall ${8 + Math.random() * 10}s linear infinite`,
            animationDelay: `${Math.random() * 6}s`,
            opacity: 0.7 + Math.random() * 0.3,
          }}
        >
          {confetti[Math.floor(Math.random() * confetti.length)]}
        </span>
      ))}
    </>
  );
}

function SunshineEffect({ intensity }: { intensity: string }) {
  const count = Math.floor(getParticleCount(intensity) / 2);
  const sparkles = ['✨', '⭐', '💫'];
  
  return (
    <>
      <style>{fallKeyframes}</style>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            fontSize: `${10 + Math.random() * 14}px`,
            animation: `sparkle ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`,
          }}
        >
          {sparkles[Math.floor(Math.random() * sparkles.length)]}
        </span>
      ))}
    </>
  );
}

function EasterEffect({ intensity }: { intensity: string }) {
  const count = getParticleCount(intensity);
  const eggs = ['🥚', '🐣', '🐰', '🌷', '🐥'];
  
  return (
    <>
      <style>{fallKeyframes}</style>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            fontSize: `${14 + Math.random() * 12}px`,
            animation: `sway-fall ${10 + Math.random() * 12}s linear infinite`,
            animationDelay: `${Math.random() * 8}s`,
            opacity: 0.6 + Math.random() * 0.4,
          }}
        >
          {eggs[Math.floor(Math.random() * eggs.length)]}
        </span>
      ))}
    </>
  );
}

function HalloweenEffect({ intensity }: { intensity: string }) {
  const count = getParticleCount(intensity);
  const spooky = ['🎃', '👻', '🦇', '🕷️', '💀'];
  
  return (
    <>
      <style>{fallKeyframes}</style>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            fontSize: `${14 + Math.random() * 14}px`,
            animation: `sway-fall ${9 + Math.random() * 12}s linear infinite`,
            animationDelay: `${Math.random() * 8}s`,
            opacity: 0.6 + Math.random() * 0.4,
          }}
        >
          {spooky[Math.floor(Math.random() * spooky.length)]}
        </span>
      ))}
    </>
  );
}

export default SeasonalDecorations;
